import axios from 'axios';

const LOCAL_API_URL = 'http://localhost:3003/api';

const isLocalHostname = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1';

const normalizeApiUrl = (url: string): string => url.replace(/\/$/, '');

const resolveApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL?.trim();

  if (typeof window === 'undefined') {
    return normalizeApiUrl(envUrl || LOCAL_API_URL);
  }

  const browserIsLocal = isLocalHostname(window.location.hostname);
  const originApi = `${window.location.origin.replace(/\/$/, '')}/api`;

  if (!envUrl) {
    return normalizeApiUrl(browserIsLocal ? LOCAL_API_URL : originApi);
  }

  try {
    const envHost = new URL(envUrl).hostname;
    if (!browserIsLocal && isLocalHostname(envHost)) {
      return normalizeApiUrl(originApi);
    }
  } catch {
    if (envUrl.startsWith('/')) {
      return normalizeApiUrl(`${window.location.origin}${envUrl}`);
    }

    return normalizeApiUrl(envUrl);
  }

  return normalizeApiUrl(envUrl);
};

export const API_URL = resolveApiUrl();

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface Business {
  id: string;
  name: string;
  category: string;
  city: string;
  websiteUrl?: string;
  description?: string;
  utp?: string;
  yandexMapsUrl?: string;
  yandexOrgId?: string;
  trackedSearchQuery?: string;
  metrikaCounterId?: string;
  twogisUrl?: string;
  twogisOrgId?: string;
  googlePlaceId?: string;
  // Brief fields
  targetAudience?: string;
  tone?: string;
  advantages?: string[];
  reviewsCount?: number;
  servicesCount?: number;
  averageRating?: number;
  createdAt?: string;
}

export interface CreateBusinessInput {
  name: string;
  category: string;
  city: string;
  websiteUrl?: string;
  description?: string;
  utp?: string;
  yandexMapsUrl?: string;
  trackedSearchQuery?: string;
  metrikaCounterId?: string;
  twogisUrl?: string;
  // Brief fields
  targetAudience?: string;
  tone?: string;
  advantages?: string[];
}

// ==================== Reviews Types ====================

export type ReviewStatus = 'NEW' | 'RESPONDED' | 'IGNORED';
export type ReviewSource = 'YANDEX' | 'TWOGIS' | 'GOOGLE' | 'MANUAL' | 'IMPORT';
export type Sentiment = 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
export type ReputationPeriod = '7d' | '30d' | '90d' | 'year';

export interface Review {
  id: string;
  businessId: string;
  externalId?: string;
  authorName: string;
  rating: number;
  text: string;
  publishedAt: string;
  source: ReviewSource;
  sentiment?: Sentiment;
  topics?: string[];
  status: ReviewStatus;
  response?: string;
  respondedAt?: string;
  aiResponses?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ReviewDeliveryInfo {
  attempted: boolean;
  published: boolean;
  channel: ReviewSource;
  message: string;
}

export const reviewsApi = {
  getByBusiness: (businessId: string, status?: ReviewStatus, source?: ReviewSource) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    const qs = params.toString();
    return api.get<{ success: boolean; data: { reviews: Review[]; total: number; statusCounts: Record<ReviewStatus, number> } }>(
      `/reviews/business/${businessId}${qs ? `?${qs}` : ''}`
    );
  },

  sync: (businessId: string) =>
    api.post<{ success: boolean; data: { synced: number; newReviews: number; message: string } }>(
      `/reviews/business/${businessId}/sync`
    ),

  create: (input: { businessId: string; authorName: string; rating: number; text: string; publishedAt?: string }) =>
    api.post<{ success: boolean; data: Review }>('/reviews', input),

  generateResponse: (reviewId: string) =>
    api.post<{ success: boolean; data: { responses: string[] } }>(`/reviews/${reviewId}/generate-response`, {}),

  saveResponse: (reviewId: string, response: string) =>
    api.put<{ success: boolean; data: { review: Review; delivery: ReviewDeliveryInfo } }>(`/reviews/${reviewId}/respond`, { response }),

  ignore: (reviewId: string) =>
    api.put<{ success: boolean; data: Review }>(`/reviews/${reviewId}/ignore`, {}),

  markResponded: (reviewId: string) =>
    api.put<{ success: boolean; data: Review }>(`/reviews/${reviewId}/mark-responded`, {}),

  importText: (businessId: string, text: string) =>
    api.post<{ success: boolean; data: { imported: number; reviews: Review[]; message: string } }>(
      '/reviews/import-text', { businessId, text }
    ),

  delete: (reviewId: string) => api.delete(`/reviews/${reviewId}`),

  getAnalytics: (businessId: string) =>
    api.get<{ success: boolean; data: ReviewAnalytics }>(`/reviews/analytics/${businessId}`),

  getReputationTrend: (businessId: string, period: ReputationPeriod = '30d') =>
    api.get<{ success: boolean; data: ReputationTrendPoint[] }>(
      `/reviews/reputation-trend/${businessId}?period=${period}`
    ),
};

export interface ReviewAnalytics {
  totalReviews: number;
  averageRating: number;
  responseRate: number;
  ratingDistribution: Record<number, number>;
  ratingTrend: Array<{ month: string; avg: number; count: number }>;
  sentimentBreakdown: { positive: number; negative: number; neutral: number; unknown: number };
  topTopics: Array<{ topic: string; count: number }>;
  sourceBreakdown: Array<{ source: string; count: number }>;
}

export interface ReputationTrendPoint {
  date: string;
  avg: number;
  count: number;
}

// ==================== Services Types ====================

export interface Service {
  id: string;
  businessId: string;
  title: string;
  category?: string;
  websiteUrl?: string;
  shortDesc: string;
  longDesc: string;
  keywords: string[];
  imageUrl?: string;
  imagePrompt?: string;
  isEdited: boolean;
  generationId?: string;
  createdAt: string;
  updatedAt: string;
}

export const servicesApi = {
  getByBusiness: (businessId: string) =>
    api.get<{ success: boolean; data: Service[] }>(`/services/business/${businessId}`),

  getYandexYmlUrl: (businessId: string) =>
    `${API_URL.replace(/\/$/, '')}/services/business/${businessId}/yandex-yml`,

  generate: (businessId: string, count?: number) =>
    api.post<{ success: boolean; data: Service[] }>('/services/generate', { businessId, count }),

  update: (id: string, data: {
    title?: string;
    category?: string;
    websiteUrl?: string;
    shortDesc?: string;
    longDesc?: string;
    keywords?: string[];
  }) =>
    api.put<{ success: boolean; data: Service }>(`/services/${id}`, data),

  delete: (id: string) => api.delete(`/services/${id}`),

  regenerateImage: (id: string) =>
    api.post<{ success: boolean; data: Service }>(`/services/${id}/regenerate-image`, {}),
};

export interface KeywordPosition {
  keyword: string;
  currentPosition: number;
  previousPosition: number;
  change: number;
  views: number;
}

export type ReportType = 'MAPS' | 'ADS' | 'KEYWORDS';

export interface Report {
  id: string;
  businessId: string;
  type: ReportType;
  period: string;
  views: number;
  phoneCalls: number;
  routes: number;
  websiteClicks: number;
  searchImpressions: number;
  categoryImpressions: number;
  newReviews: number;
  averageRating: number;
  totalReviews: number;
  respondedReviews: number;
  stars5: number;
  stars4: number;
  stars3: number;
  stars2: number;
  stars1: number;
  trafficSearch: number;
  trafficMaps: number;
  trafficNavigator: number;
  trafficDirect: number;
  keywordPositions?: KeywordPosition[];
  // Ads-specific
  adImpressions?: number;
  adClicks?: number;
  adCtr?: number;
  adSpend?: number;
  aiSummary?: string;
  aiAchievements?: string[];
  aiRecommendations?: string[];
  aiComparison?: string;
  createdAt: string;
  updatedAt: string;
  business?: Business;
}

export interface CreateReportInput {
  businessId: string;
  period: string;
  type?: ReportType;
  views?: number;
  phoneCalls?: number;
  routes?: number;
  websiteClicks?: number;
  searchImpressions?: number;
  categoryImpressions?: number;
  newReviews?: number;
  averageRating?: number;
  totalReviews?: number;
  respondedReviews?: number;
  stars5?: number;
  stars4?: number;
  stars3?: number;
  stars2?: number;
  stars1?: number;
  trafficSearch?: number;
  trafficMaps?: number;
  trafficNavigator?: number;
  trafficDirect?: number;
  keywordPositions?: KeywordPosition[];
  adImpressions?: number;
  adClicks?: number;
  adCtr?: number;
  adSpend?: number;
}

export interface SharedReportLinkData {
  url: string;
  expiresInDays: number;
}

export interface SharedReportSnapshot {
  business: {
    id: string;
    name: string;
    category: string;
    city: string;
  };
  period: {
    dateFrom: string;
    dateTo: string;
  };
  counterId: string;
  live: boolean;
  generatedAt: string;
  metrics: MetrikaPrefillData;
}

export interface SharedSavedReportsSnapshot {
  business: {
    id: string;
    name: string;
    category: string;
    city: string;
  };
  periods: string[];
  selectedPeriod: string | null;
  reportsByType: {
    MAPS: Report | null;
    ADS: Report | null;
    KEYWORDS: Report | null;
  };
}

// API functions
export const reportsApi = {
  getBusinesses: () => api.get<{ success: boolean; data: Business[] }>('/reports/businesses'),

  getReportsByBusiness: (businessId: string) =>
    api.get<{ success: boolean; data: Report[] }>(`/reports/business/${businessId}`),

  getReport: (id: string) => api.get<{ success: boolean; data: Report }>(`/reports/${id}`),

  upsertReport: (input: CreateReportInput) =>
    api.post<{ success: boolean; data: Report }>('/reports', input),

  createShareLink: (businessId: string) =>
    api.post<{ success: boolean; data: SharedReportLinkData }>('/reports/share-link', { businessId }),

  getSharedReport: (token: string, params: { dateFrom?: string; dateTo?: string }) => {
    const query = new URLSearchParams();
    if (params.dateFrom?.trim()) {
      query.set('dateFrom', params.dateFrom.trim());
    }
    if (params.dateTo?.trim()) {
      query.set('dateTo', params.dateTo.trim());
    }

    return api.get<{ success: boolean; data: SharedReportSnapshot }>(
      `/reports/shared/${token}?${query.toString()}`,
    );
  },

  getSharedSavedReports: (token: string, params: { period?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.period?.trim()) {
      query.set('period', params.period.trim());
    }

    const qs = query.toString();
    return api.get<{ success: boolean; data: SharedSavedReportsSnapshot }>(
      `/reports/shared/${token}/reports${qs ? `?${qs}` : ''}`,
    );
  },

  generateAISummary: (id: string) =>
    api.post<{ success: boolean; data: { summary: string; achievements: string[]; recommendations: string[]; comparison: string } }>(`/reports/${id}/ai-summary`),

  getPdfUrl: (id: string) => `${API_URL}/reports/${id}/pdf`,

  deleteReport: (id: string) => api.delete(`/reports/${id}`),
};

// ==================== Content Types ====================

export type ContentItemType = 'ARTICLE' | 'HEADLINE' | 'PROMOTION' | 'NEWS';

export interface ContentItem {
  id: string;
  businessId: string;
  type: ContentItemType;
  title: string;
  content: string;
  brief?: string;
  generationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateContentInput {
  businessId: string;
  type: ContentItemType;
  brief: string;
  keywords?: string[];
}

export interface ContentYandexDeliveryInfo {
  attempted: boolean;
  published: boolean;
  mode: 'webhook' | 'disabled';
  message: string;
}

export interface PublishContentToYandexResult {
  contentItem: ContentItem;
  creativeUrl: string | null;
  creativeWarning: string | null;
  delivery: ContentYandexDeliveryInfo;
}

export interface SuggestPublicationTopicsInput {
  businessId: string;
  count?: number;
}

export interface GeneratePublicationDraftInput {
  businessId: string;
  topic: string;
}

export interface PublicationDraftResult {
  topic: string;
  contentItem: ContentItem;
  creativeUrl: string | null;
  creativeWarning: string | null;
}

export interface GeneratePromotionDraftInput {
  businessId: string;
  task: string;
  variantsCount?: number;
  referenceImageUrls?: string[];
}

export interface PromotionCreativeVariant {
  index: number;
  url: string;
}

export interface PromotionDraftResult {
  task: string;
  contentItem: ContentItem;
  creativeUrl: string | null;
  creatives: PromotionCreativeVariant[];
  creativeWarning: string | null;
  websiteUrlUsed: string | null;
}

export interface UploadPromotionReferenceInput {
  businessId: string;
  fileName: string;
  dataUrl: string;
}

export interface UploadedPromotionReference {
  url: string;
  filename: string;
  originalFilename: string;
}

export const contentApi = {
  getByBusiness: (businessId: string, type?: ContentItemType) =>
    api.get<{ success: boolean; data: ContentItem[] }>(
      `/content/business/${businessId}${type ? `?type=${type}` : ''}`,
    ),

  generate: (input: GenerateContentInput) =>
    api.post<{ success: boolean; data: ContentItem }>('/content/generate', input),

  update: (id: string, data: { title?: string; content?: string }) =>
    api.put<{ success: boolean; data: ContentItem }>(`/content/${id}`, data),

  publishToYandex: (id: string) =>
    api.post<{ success: boolean; data: PublishContentToYandexResult }>(`/content/${id}/publish-yandex`, {}),

  suggestPublicationTopics: (input: SuggestPublicationTopicsInput) =>
    api.post<{ success: boolean; data: string[] }>('/content/publication-topics', input),

  generatePublicationDraft: (input: GeneratePublicationDraftInput) =>
    api.post<{ success: boolean; data: PublicationDraftResult }>('/content/publication-draft', input),

  generatePromotionDraft: (input: GeneratePromotionDraftInput) =>
    api.post<{ success: boolean; data: PromotionDraftResult }>('/content/promotion-draft', input),

  uploadPromotionReference: (input: UploadPromotionReferenceInput) =>
    api.post<{ success: boolean; data: UploadedPromotionReference }>('/content/promotion-reference', input),

  delete: (id: string) => api.delete(`/content/${id}`),
};

// ==================== Keywords Types ====================

export interface Keyword {
  id: string;
  businessId: string;
  keyword: string;
  cluster?: string;
  frequency?: number;
  currentPosition?: number;
  previousPosition?: number;
  positionDelta?: number;
  isPFKeyword: boolean;
  isManual: boolean;
  isTrackedPosition: boolean;
  createdAt: string;
  updatedAt: string;
}

export const keywordsApi = {
  getByBusiness: (businessId: string) =>
    api.get<{ success: boolean; data: Keyword[] }>(`/keywords/business/${businessId}`),

  generate: (businessId: string) =>
    api.post<{ success: boolean; data: Keyword[] }>('/keywords/generate', { businessId }),

  addManual: (input: { businessId: string; keyword: string; cluster?: string; frequency?: number }) =>
    api.post<{ success: boolean; data: Keyword }>('/keywords', input),

  updatePositions: (updates: Array<{ id: string; currentPosition: number; previousPosition: number }>) =>
    api.put<{ success: boolean }>('/keywords/positions', { updates }),

  togglePF: (id: string) =>
    api.put<{ success: boolean; data: Keyword }>(`/keywords/${id}/pf`, {}),

  toggleTrackPosition: (id: string) =>
    api.put<{ success: boolean; data: Keyword }>(`/keywords/${id}/track-position`, {}),

  delete: (id: string) => api.delete(`/keywords/${id}`),

  exportPFUrl: (businessId: string, format: 'txt' | 'csv') =>
    `${API_URL}/keywords/business/${businessId}/export-pf?format=${format}`,
};

// ==================== Knowledge Base Types ====================

export type KBCategory = 'REVIEW_RESPONSE' | 'ARTICLE' | 'PROMOTION' | 'HEADLINE' | 'NEWS' | 'SERVICES';

export interface KBExample {
  id: string;
  category: KBCategory;
  title: string;
  userInput: string;
  aiOutput: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKBExampleInput {
  category: KBCategory;
  title: string;
  userInput: string;
  aiOutput: string;
  isActive?: boolean;
}

export const knowledgeBaseApi = {
  getAll: () => api.get<{ success: boolean; data: KBExample[] }>('/knowledge-base'),

  create: (input: CreateKBExampleInput) =>
    api.post<{ success: boolean; data: KBExample }>('/knowledge-base', input),

  update: (id: string, data: Partial<CreateKBExampleInput>) =>
    api.put<{ success: boolean; data: KBExample }>(`/knowledge-base/${id}`, data),

  delete: (id: string) => api.delete(`/knowledge-base/${id}`),
};

// ==================== Competitor Types ====================

export interface Competitor {
  id: string;
  businessId: string;
  name: string;
  yandexMapsUrl: string;
  yandexOrgId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitorPositionHistoryPoint {
  date: string;
  position: number | null;
}

export interface KeywordPositionData {
  keywordId: string;
  keyword: string;
  position: number | null;
  previousPosition: number | null;
  delta: number | null;
  checkedAt: string | null;
}

export interface KeywordPositionsResult {
  averagePosition: number | null;
  keywords: KeywordPositionData[];
}

export const competitorApi = {
  getAll: (businessId: string) =>
    api.get<{ success: boolean; data: Competitor[] }>(`/competitors?businessId=${businessId}`),

  create: (data: { businessId: string; name: string; yandexMapsUrl: string; yandexOrgId?: string }) =>
    api.post<{ success: boolean; data: Competitor }>('/competitors', data),

  remove: (id: string) => api.delete(`/competitors/${id}`),

  getPositionHistory: (businessId: string, period: ReputationPeriod = '30d') =>
    api.get<{ success: boolean; data: CompetitorPositionHistoryPoint[] }>(
      `/competitors/position-history/${businessId}?period=${period}`
    ),

  getKeywordPositions: (businessId: string) =>
    api.get<{ success: boolean; data: KeywordPositionsResult }>(
      `/competitors/keyword-positions/${businessId}`
    ),
};

// ==================== Review Templates Types ====================

export interface ReviewTemplate {
  id: string;
  userId: string;
  name: string;
  category: Sentiment;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReviewTemplateInput {
  name: string;
  category: Sentiment;
  content: string;
}

export const reviewTemplateApi = {
  getAll: () =>
    api.get<{ success: boolean; data: ReviewTemplate[] }>('/review-templates'),

  create: (data: CreateReviewTemplateInput) =>
    api.post<{ success: boolean; data: ReviewTemplate }>('/review-templates', data),

  update: (id: string, data: Partial<CreateReviewTemplateInput>) =>
    api.put<{ success: boolean; data: ReviewTemplate }>(`/review-templates/${id}`, data),

  remove: (id: string) => api.delete(`/review-templates/${id}`),
};

// ==================== Yandex OAuth Types ====================

export interface MetrikaPrefillData {
  websiteClicks?: number;
  views?: number;
  phoneCalls?: number;
  routes?: number;
  searchImpressions?: number;
  categoryImpressions?: number;
  trafficSearch?: number;
  trafficMaps?: number;
  trafficNavigator?: number;
  trafficDirect?: number;
  adSpend?: number;
  adConversions?: number;
  adCostPerConversion?: number;
  trackedGoalId?: string;
  trackedGoalName?: string;
}

export interface YandexMetrikaCounter {
  id: string;
  name: string;
  site?: string;
  permission?: string;
}

export interface YandexMetrikaGoal {
  id: string;
  name: string;
}

export type YandexOAuthAccountType = 'PRIMARY' | 'ADS';

export interface YandexBusinessAnalyticsData {
  period: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  counterId: string;
  savedCounterId: string | null;
  metrics: MetrikaPrefillData;
}

export interface YandexCompetitorTopResult {
  orgId: string;
  name: string;
  url: string;
  position: number;
}

export interface YandexTrackedCompetitorPosition {
  competitorId: string;
  name: string;
  orgId: string | null;
  yandexMapsUrl: string;
  position: number | null;
  foundInTop: boolean;
}

export interface YandexCompetitorPositionsData {
  query: string;
  ownOrgId: string | null;
  ownPosition: number | null;
  topResults: YandexCompetitorTopResult[];
  trackedCompetitors: YandexTrackedCompetitorPosition[];
  note?: string;
  checkedAt: string;
}

export interface YandexCompetitorBusinessStatsRow {
  name: string;
  views: number | null;
  phoneCalls: number | null;
  routes: number | null;
  websiteClicks: number | null;
  position: number;
}

export interface YandexCompetitorBusinessStatsData {
  sourceUrl: string;
  periodDays: number;
  rows: YandexCompetitorBusinessStatsRow[];
  checkedAt: string;
  note?: string;
}

export interface MetrikaDailyTrendPoint {
  date: string;
  visits: number;
  goalReaches?: number;
}

export const yandexApi = {
  getStatus: (businessId: string) =>
    api.get<{
      success: boolean;
      data: {
        connected: boolean;
        adsConnected?: boolean;
        reviewPublishingReady?: boolean;
        reviewPublishingMode?: 'webhook' | 'owner-session' | 'disabled';
        reviewPublishingHint?: string;
        publicationPublishingReady?: boolean;
        publicationPublishingMode?: 'webhook' | 'disabled';
        publicationPublishingHint?: string;
      };
    }>(`/yandex/status/${businessId}`),

  getOAuthStartUrl: (
    businessId: string,
    redirectTo?: string,
    tokenType: YandexOAuthAccountType = 'PRIMARY',
  ) => {
    const params = new URLSearchParams({ businessId });
    if (redirectTo) {
      params.set('redirectTo', redirectTo);
    }
    if (tokenType === 'ADS') {
      params.set('tokenType', 'ads');
    }
    return `${API_URL}/yandex/oauth/start?${params.toString()}`;
  },

  disconnect: (
    businessId: string,
    tokenType: YandexOAuthAccountType = 'PRIMARY',
  ) => {
    const query = tokenType === 'ADS' ? '?tokenType=ads' : '';
    return api.delete<{ success: boolean }>(`/yandex/disconnect/${businessId}${query}`);
  },

  prefill: (businessId: string, counterId: string, period: string) =>
    api.post<{ success: boolean; data: MetrikaPrefillData }>('/yandex/metrika/prefill', {
      businessId,
      counterId,
      period,
    }),

  getMetrikaCounters: (
    businessId: string,
    accountType: YandexOAuthAccountType = 'PRIMARY',
  ) => {
    const query = accountType === 'ADS' ? '?accountType=ads' : '';
    return api.get<{ success: boolean; data: YandexMetrikaCounter[] }>(
      `/yandex/metrika/counters/${businessId}${query}`,
    );
  },

  getMetrikaGoals: (
    businessId: string,
    counterId: string,
    accountType: YandexOAuthAccountType = 'PRIMARY',
  ) => {
    const query = new URLSearchParams({ counterId: counterId.trim() });
    if (accountType === 'ADS') {
      query.set('accountType', 'ads');
    }
    return api.get<{ success: boolean; data: YandexMetrikaGoal[] }>(
      `/yandex/metrika/goals/${businessId}?${query.toString()}`,
    );
  },

  getBusinessAnalytics: (
    businessId: string,
    params: {
      period?: string;
      dateFrom?: string;
      dateTo?: string;
      counterId?: string;
      goalId?: string;
      saveCounter?: boolean;
      accountType?: YandexOAuthAccountType;
    },
  ) => {
    const query = new URLSearchParams();
    if (params.period?.trim()) {
      query.set('period', params.period.trim());
    }
    if (params.dateFrom?.trim()) {
      query.set('dateFrom', params.dateFrom.trim());
    }
    if (params.dateTo?.trim()) {
      query.set('dateTo', params.dateTo.trim());
    }
    if (params.counterId?.trim()) {
      query.set('counterId', params.counterId.trim());
    }
    if (params.goalId?.trim()) {
      query.set('goalId', params.goalId.trim());
    }
    if (params.accountType === 'ADS') {
      query.set('accountType', 'ads');
    }
    if (params.saveCounter) {
      query.set('saveCounter', 'true');
    }

    return api.get<{ success: boolean; data: YandexBusinessAnalyticsData }>(
      `/yandex/business-analytics/${businessId}?${query.toString()}`,
    );
  },

  getMetrikaDailyTrend: (
    businessId: string,
    params: { dateFrom: string; dateTo: string; counterId?: string; goalId?: string },
  ) => {
    const query = new URLSearchParams({ dateFrom: params.dateFrom, dateTo: params.dateTo });
    if (params.counterId?.trim()) query.set('counterId', params.counterId.trim());
    if (params.goalId?.trim()) query.set('goalId', params.goalId.trim());
    return api.get<{ success: boolean; data: MetrikaDailyTrendPoint[] }>(
      `/yandex/metrika/daily-trend/${businessId}?${query.toString()}`,
    );
  },

  getCompetitorPositions: (
    businessId: string,
    params?: { query?: string; limit?: number },
  ) => {
    const query = new URLSearchParams();
    if (params?.query?.trim()) {
      query.set('query', params.query.trim());
    }
    if (typeof params?.limit === 'number' && Number.isFinite(params.limit)) {
      query.set('limit', String(params.limit));
    }

    const search = query.toString();
    return api.get<{ success: boolean; data: YandexCompetitorPositionsData }>(
      `/yandex/competitor-positions/${businessId}${search ? `?${search}` : ''}`,
    );
  },

  getCompetitorsBusinessStats: (businessId: string) =>
    api.get<{ success: boolean; data: YandexCompetitorBusinessStatsData }>(
      `/yandex/competitors-business-stats/${businessId}`,
    ),
};

// ==================== Dashboard Types ====================

export interface BusinessOverview {
  id: string;
  name: string;
  category: string;
  city: string;
  reviewsCount: number;
  newReviewsCount: number;
  averageRating: number;
  servicesCount: number;
  keywordsCount: number;
  keywordsUp: number;
  lastContentDate: string | null;
  lastReportPeriod: string | null;
}

export const dashboardApi = {
  getOverview: () =>
    api.get<{ success: boolean; data: BusinessOverview[] }>('/dashboard/overview'),
};

// ==================== AI Recommendations ====================

export interface AIRecommendation {
  id: string;
  businessId: string;
  category: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  createdAt: string;
}

// ==================== Business Detail Types ====================

export interface BusinessDetail {
  id: string;
  name: string;
  category: string;
  city: string;
  websiteUrl?: string;
  description?: string;
  yandexMapsUrl?: string;
  // Counts
  reviewsCount: number;
  newReviewsCount: number;
  respondedReviewsCount: number;
  averageRating: number;
  servicesCount: number;
  keywordsCount: number;
  keywordsUp: number;
  // Recent data
  newReviews: Review[];
  recentContent: ContentItem[];
  latestReport: Report | null;
  recommendations: AIRecommendation[];
}

// Business API
export const businessApi = {
  getAll: () => api.get<{ success: boolean; data: Business[] }>('/businesses'),

  getById: (id: string) => api.get<{ success: boolean; data: Business }>(`/businesses/${id}`),

  create: (input: CreateBusinessInput) =>
    api.post<{ success: boolean; data: Business }>('/businesses', input),

  update: (id: string, input: Partial<CreateBusinessInput>) =>
    api.put<{ success: boolean; data: Business }>(`/businesses/${id}`, input),

  delete: (id: string) => api.delete(`/businesses/${id}`),

  generateBrief: (id: string) =>
    api.post<{ success: boolean; data: Business }>(`/businesses/${id}/generate-brief`, {}),

  getDetail: (id: string) =>
    api.get<{ success: boolean; data: BusinessDetail }>(`/businesses/${id}/detail`),

  getRecommendations: (id: string) =>
    api.get<{ success: boolean; data: AIRecommendation[] }>(`/businesses/${id}/recommendations`),

  generateRecommendations: (id: string) =>
    api.post<{ success: boolean; data: AIRecommendation[] }>(`/businesses/${id}/recommendations`, {}),

  updateRecommendation: (recId: string, status: string) =>
    api.patch<{ success: boolean; data: AIRecommendation }>(`/businesses/recommendations/${recId}`, { status }),
};
